import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ApiError, errorMessage } from "../../api";
import { Modal } from "../../components/bits";
import { Icon } from "../../components/icons";
import { toast } from "../../store/app";
import { useApp } from "../../store/hooks";

export const ROLES = ["owner", "admin", "member", "guest"] as const;

/** Load data for the current org; reloads when the org changes. */
export function useOrgData<T>(load: (orgId: string) => Promise<T>) {
  const orgId = useApp((s) => s.orgId);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const reload = useCallback(() => {
    if (!orgId) return;
    setError(null);
    load(orgId)
      .then((d) => setData(d))
      .catch((e) => {
        if (e instanceof ApiError && e.status === 403) setForbidden(true);
        setError(errorMessage(e));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);
  useEffect(reload, [reload]);
  return { orgId, data, setData, error, forbidden, reload };
}

/** A secret shown exactly once, with a copy button. */
export function OneTimeSecret({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="secret-box" role="status">
      <p className="small">
        <strong>{label}</strong> – shown only once. Copy it now and share it securely.
      </p>
      <div className="secret-row">
        <input readOnly value={value} aria-label={label} onFocus={(e) => e.currentTarget.select()} className="mono" />
        <button
          type="button"
          className="btn small"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setCopied(true);
              toast("Copied to clipboard", "success", 2500);
            } catch {
              toast("Copy failed – select the text and copy it manually", "error");
            }
          }}
        >
          <Icon name="copy" size={14} />
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  danger,
  onConfirm,
  onClose,
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={title} onClose={onClose}>
      <div className="form">
        <div className="confirm-text">{children}</div>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn ${danger ? "danger-solid" : "primary"}`}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function Forbidden() {
  return (
    <div className="page forbidden">
      <div className="forbidden-card">
        <Icon name="shield" size={40} />
        <h1>403 – Admins only</h1>
        <p className="muted">
          The admin panel is available to organization owners and admins. Ask an owner if you need access.
        </p>
      </div>
    </div>
  );
}

export function StatCard({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
      {hint && <div className="muted small">{hint}</div>}
    </div>
  );
}
